/** Brand identity RECORDS -- one exported record per brand this codebase can be built as. The SELECTION between them is
 *  not made here: `const/brand-registry.ts` owns `ACTIVE_BRAND_NAME` and resolves `ACTIVE_BRAND`, so this leaf stays a
 *  pure data home with no import that can close a cycle, and no consumer can accidentally depend on "the brand that
 *  happens to be first".
 *
 *  NO CYCLE-CLOSING IMPORT, ON PURPOSE. Until 2026-08-20 this leaf imported `ZARAZ_CUSTOM_EVENT_ENDPOINT` from
 *  `const/seo-http.ts`, which forbade the reverse edge and left `FEDERA_PROD_HOSTNAME` stranded as a second spelling of
 *  `domains.primary`. The endpoint is brand data (its path carries the brand word), so it is DECLARED here and
 *  `const/seo-http.ts` now reads the brand record instead -- the cycle is gone in the only direction that can stay gone.
 *  The one import is `const/page-title.ts`, the fleet's tab-title grammar, which imports only types and so can never
 *  close a cycle back here.
 *
 *  DIRECT IMPORTERS OF `FEDERA_BRAND` ARE THE REGISTRY LEAF, THE PER-BRAND LEAVES THAT ARE FEDERA, AND THE BRAND TESTS
 *  -- nothing else. A PRODUCT SURFACE that names one brand cannot be re-skinned, so every one of those reads
 *  `ACTIVE_BRAND` (`const/brand-registry.ts`) instead. A leaf that IS a named brand is the opposite case and must NOT
 *  read the selection: `const/brand-assets.ts` composes the federa manifest's rendered copy off this record, exactly as
 *  `brand/federa/tokens/brand-manifest.ts` reads `BRAND_NAMES.FEDERA` for its own `brandName` and
 *  `brand/federa/assets/images.ts` reads `BRAND_ASSETS[BRAND_NAMES.FEDERA]` for its two url spellings, because a named
 *  record reading the SELECTION renders the other brand's identity under this brand's name the moment a second brand
 *  exists.
 */

import { productTitleTemplate } from './page-title.ts';

/**
 * White-label brand identity. Every brand-tied string a consumer might embed in user-visible output, contracts,
 * payments, or DOM globals must reside on this shape so a single `ACTIVE_BRAND_NAME` swap re-skins the app.
 *
 * EVERY MEMBER IS REQUIRED -- there is no partial brand record. An optional member would let a second brand ship with a
 * hole that falls back to the incumbent's string at runtime, which is the exact failure white-labelling exists to
 * prevent: the fallback is invisible in review, compiles clean, and surfaces as the wrong company's name on a contract.
 */
export interface BrandIdentity {
	readonly name: string;
	/** Legal entity name used on wire instructions and as buyer-of-record. */
	readonly legalName: string;
	/** Consumer-product name -- the marketplace app's own name, which is NOT the bare brand name ('Federa Explore' vs 'Federa'). Used as the SEO site name, `applicationName`, PWA `name` and the tail of {@link BrandIdentity.titleTemplate}. */
	readonly productName: string;
	/** Compact product name for space-constrained surfaces: PWA `short_name`, schema.org `alternateName`, social-image captions. */
	readonly shortName: string;
	/** One-line positioning statement; also the Open Graph image alt text. */
	readonly tagline: string;
	/** Paragraph-length product description for `<meta name="description">`, PWA `description` and the crawler-facing feeds. */
	readonly description: string;
	/** Document-title pattern with Next's `%s` page-title placeholder (`metadata.title.template`). Composed from {@link BrandIdentity.productName} so the product name is spelled once. */
	readonly titleTemplate: string;
	/** Hosts WITHOUT protocol, e.g. 'federa.com' / 'admin-dev.federa.com'. */
	readonly domains: {
		readonly primary: string;
		readonly admin: string;
		readonly app: string;
		readonly devPrimary: string;
		readonly devAdmin: string;
	};
	/** Custom URL scheme for native deep links, WITHOUT the `://` (composed against `domains.app` by `getDeepLink`). */
	readonly deepLinkScheme: string;
	/** Public origins permitted by CORS / origin allowlists. */
	readonly allowedOrigins: readonly string[];
	readonly contactEmails: {
		readonly info: string;
		readonly admin: string;
		readonly support: string;
	};
	readonly socialProfiles: readonly string[];
	/** Stripe Connect business profile URL (account creation). */
	readonly stripeConnectBusinessUrl: string;
	/** Account name written to wire-transfer instructions. */
	readonly stripeAccountName: string;
	/** Postal address used on wire-transfer instructions. */
	readonly postalAddress: string;
	readonly contractTypeDefault: string;
	/** Display label for the brand's percent-of-assessed pricing rule. */
	readonly pricingRuleName: string;
	/** Memo-type label for processing-fee ledger entries. */
	readonly processingFeeMemo: string;
	/** Buyer-of-record name on Market Maker source configs. */
	readonly marketMakerBuyerName: string;
	/** Fallback display name for users with no profile name set. */
	readonly defaultUserName: string;
	readonly zarazCustomEventEndpoint: string;
	/** Prefix for `window.__<brand>*` globals -- 'federa' yields `__federaAuth`, `__federaShell`, `__federaMFE_<slug>`. */
	readonly globalNamespacePrefix: string;
	/**
	 * MODULE SPECIFIER of the brand's font module -- a POINTER, never the font objects themselves.
	 *
	 * `next/font` loaders are a COMPILE-TIME transform: the SWC plugin reads the loader call's arguments statically and
	 * refuses anything but literals, so a font choice cannot be a value on this record and cannot be selected at
	 * runtime. Each brand therefore ships its own literal module (`brand/federa/fonts.ts`) and the record names it, so a
	 * brand swap re-points the import rather than re-typing a loader call.
	 */
	readonly fontModule: string;
	/**
	 * IMAGERY POINTERS -- registry KEYS, never asset values.
	 *
	 * Each member names an entry the brand-asset registry resolves to a CDN url or a public path for this brand. The
	 * identity record deliberately holds no url, no file path and no image id, so the asset layer stays the one place an
	 * asset is spelled and this record stays readable from every layer (a `const/` leaf may not reach an app's `public/`).
	 *
	 * Open Graph needs no members beyond `ogImage`: its `siteName`, `title`, `description` and image `alt` are already
	 * {@link BrandIdentity.productName}, {@link BrandIdentity.titleTemplate}, {@link BrandIdentity.description} and
	 * {@link BrandIdentity.tagline}, and a second copy under an `og*` name would be drift with extra steps.
	 */
	readonly assetKeys: {
		readonly logo: string;
		/** Horizontal name-mark. FEDERA has no distinct wordmark artwork today -- the registry entry stands so a brand that HAS one needs no shape change, and the maskable-icon lesson applies: claim the asset only where the artwork exists. */
		readonly wordmark: string;
		readonly favicon: string;
		readonly pwaIcon192: string;
		readonly pwaIcon512: string;
		/** Default social-card image, used when a page supplies none. */
		readonly ogImage: string;
	};
}

/** Product name held once: the record reads it directly AND composes the document-title template from it, so a rename is one edit. */
const FEDERA_PRODUCT_NAME = 'Federa Explore';

/** SSOT for the 'Federa' literals. Read it through `ACTIVE_BRAND` (const/brand-registry.ts) -- naming this record at a consumer pins that consumer to one brand. */
export const FEDERA_BRAND: BrandIdentity = {
	name: 'Federa',
	legalName: 'Federa Inc.',
	productName: FEDERA_PRODUCT_NAME,
	shortName: 'Federa',
	tagline: 'Real-time real estate investment marketplace',
	description:
		'Federa Explore surfaces curated real estate investment opportunities, live auctions, and deal analytics so investors can research listings, compare performance, and act with confidence.',
	titleTemplate: productTitleTemplate(FEDERA_PRODUCT_NAME),
	domains: {
		primary: 'federa.com',
		admin: 'admin.federa.com',
		app: 'federa.app',
		devPrimary: 'dev.federa.com',
		devAdmin: 'admin-dev.federa.com',
	},
	deepLinkScheme: 'federa',
	allowedOrigins: [
		'https://federa.com',
		'https://www.federa.com',
		'https://dev.federa.com',
		'https://admin.federa.com',
		'https://admin-dev.federa.com',
		'https://federa.app',
		'https://www.federa.app',
	],
	contactEmails: {
		info: 'info@federa.com',
		admin: 'admin@federa.com',
		support: 'support@federa.com',
	},
	socialProfiles: [
		'https://www.linkedin.com/company/federa',
		'https://www.youtube.com/@federa',
		'https://www.instagram.com/federa.realestate',
	],
	stripeConnectBusinessUrl: 'https://www.federa.com/',
	stripeAccountName: 'Federa Inc.',
	postalAddress: '11900 Baron Cameron Ave, Reston, VA 20190',
	contractTypeDefault: 'Federa',
	pricingRuleName: 'Federa Rule',
	processingFeeMemo: 'Federa Processing Fee',
	marketMakerBuyerName: 'Federa Inc.',
	defaultUserName: 'Federa User',
	zarazCustomEventEndpoint: '/zaraz-federa-custom-event',
	globalNamespacePrefix: 'federa',
	fontModule: '@federa/design-system/fonts',
	assetKeys: {
		logo: 'federa.logo',
		wordmark: 'federa.wordmark',
		favicon: 'federa.favicon',
		pwaIcon192: 'federa.pwaIcon192',
		pwaIcon512: 'federa.pwaIcon512',
		ogImage: 'federa.ogImage',
	},
} as const;
